import { DefaultListPageSize, EmptyView, ListControls, LoadingView, StatusBadge, DashStyles as Styles, TableWrap } from '../../components/dashboard/DashboardUI';
import { useDashboardData } from '../../components/dashboard/useDashboardData';
import { Localized } from '../../internal/Localized';
import { useDashboard } from '../../providers/DashboardProvider';
import { useLocale } from '../../providers/LocaleProvider';
import { Label } from '@app/engine';
import { FormatTimestamp } from '@shared/common';
import { BillingClient } from '@shared/frontend';
import { useEffect, useMemo, useState } from 'react';

/** File-local constants. */
const localConstants = { minorUnitsPerUnit: 100 } as const;

/**
 * Formats a minor-unit amount as a currency string in the user's selected app language.
 *
 * @param amountMinor - Amount in minor units (e.g. cents).
 * @param currency - ISO currency code.
 * @param locale - The user's selected app locale (BCP-47).
 * @returns A formatted currency string.
 */
function FormatMoney(amountMinor: number, currency: string, locale: string): string {
  const value = amountMinor / localConstants.minorUnitsPerUnit;
  try {
    return new Intl.NumberFormat(locale, { style: 'currency', currency: currency.toUpperCase() }).format(value);
  } catch {
    return `${value.toFixed(2)} ${currency.toUpperCase()}`;
  }
}

/**
 * Invoices screen: the tenant's billing invoices as a table, most recent first. Invoices come from the billing provider as one bounded per-tenant list, so search and pagination are applied
 * client-side (there is nothing to gain from a server round-trip) while still using the shared {@link ListControls} panel for a consistent look with the other list pages.
 *
 * @returns The invoices element.
 */
export function Invoices() {
  const { tenantID } = useDashboard();
  const { selectedLang } = useLocale();
  const { data, loading } = useDashboardData((signal) => BillingClient.GetInvoices(signal, tenantID!), [tenantID]);
  const [search, setSearch] = useState('');
  const [offset, setOffset] = useState(0);
  const [limit, setLimit] = useState<number>(DefaultListPageSize);

  // A new search term or page size restarts at the first page (mirrors the server-paginated pages).
  useEffect(() => setOffset(0), [search, limit]);

  const all = useMemo(() => data ?? [], [data]);
  // Client-side filter over the bounded invoice list: match the search term against the paid/unpaid state, the formatted amount, and the currency.
  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return all;
    return all.filter((invoice) => {
      const paidText = invoice.paid ? Localized.SharedPaid : Localized.SharedUnpaid;
      const amount = FormatMoney(invoice.amountDue, invoice.currency, selectedLang);
      return `${paidText} ${amount} ${invoice.currency}`.toLowerCase().includes(term);
    });
  }, [all, search, selectedLang]);
  const page = filtered.slice(offset, offset + limit);

  return (
    <>
      <ListControls
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder={Localized.InvoicesSearchPlaceholder}
        offset={offset}
        limit={limit}
        total={filtered.length}
        pageCount={page.length}
        onOffsetChange={setOffset}
        onLimitChange={setLimit}
        loading={loading}
      />
      {loading ? (
        <LoadingView />
      ) : page.length === 0 ? (
        <EmptyView message={Localized.BillingNoInvoices} />
      ) : (
        <TableWrap>
          <table className={Styles.table}>
            <thead>
              <tr>
                <th>
                  <Label text={Localized.BillingInvoiceDate} />
                </th>
                <th>
                  <Label text={Localized.BillingInvoiceAmount} />
                </th>
                <th>
                  <Label text={Localized.SharedPaid} />
                </th>
                <th />
              </tr>
            </thead>
            <tbody>
              {page.map((invoice) => (
                <tr key={invoice.id}>
                  <td>
                    <Label text={FormatTimestamp(invoice.issuedAt ?? invoice.createdAt)} />
                  </td>
                  <td>
                    <Label text={FormatMoney(invoice.amountDue, invoice.currency, selectedLang)} />
                  </td>
                  <td>
                    <StatusBadge status={invoice.paid ? 'paid' : 'unpaid'} />
                  </td>
                  <td>
                    {invoice.hostedInvoiceURL ? (
                      <a className={`${Styles.btn} ${Styles.btnSmall}`} href={invoice.hostedInvoiceURL} target="_blank" rel="noreferrer">
                        <Label text={Localized.BillingViewInvoice} />
                      </a>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableWrap>
      )}
    </>
  );
}
